import { useState, useEffect, useContext } from 'react';
import Assessments from '@/utils/api/odas/assessments';
import { LocationContext } from '@/context/location.context';
import { AccountContext } from '@/context/account.context';
import { languageContext } from '@/context/language.context';

const defaultProps = { agencyClientId: null, includeStatus: false, includeInactive: false };

export const useAssessments = ({ agencyClientId, includeStatus, allowDuplicates, includeInactive } = defaultProps) => {
  const [assessments, setAssessments] = useState([]);
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(true);
  const [userAssessments, setUserAssessments] = useState([]);
  const [orgAssessments, setOrgAssessments] = useState([]);
  const [publicAssessmentError, setPublicAssessmentError] = useState(null);
  const [privateAssessmentError, setPrivateAssessmentError] = useState(null);
  const [orgAssessmentError, setOrgAssessmentError] = useState(null);
  const account = useContext(AccountContext);
  const locationContext = useContext(LocationContext);

  const { hasActiveSession, isSessionRetrieved } = account;
  const region = account.data?.region || locationContext.region;
  // Both account data and stored location resolve asynchronously.
  // Wait for both before fetching assessments.
  const isRegionSettled = isSessionRetrieved && locationContext.region !== undefined;
  const { currentLanguage } = useContext(languageContext);

  async function loadPublicAssessments() {
    try {
      const result = await Assessments.loadPublicAssessments({ locale: currentLanguage, with_status: 'any', include_status: includeStatus, region });
      setAssessments(result.data?.assessments || []);
    } finally {
      setIsLoadingAssessments(false);
    }
  }

  async function loadPrivateAssessments() {
    if (hasActiveSession) {
      let opts = { agencyClientId, locale: currentLanguage, with_status: 'any', region };
      if (allowDuplicates) {
        opts.allow_duplicates = 'true';
      }
      const result = await Assessments.loadUserAssessments(opts);
      setUserAssessments(result.data?.assessments || []);
    } else {
      setUserAssessments([]);
    }
  }

  async function loadOrgAssessments() {
    if (hasActiveSession) {
      const options = { only_authored: 'true', locale: currentLanguage };
      if (includeInactive) {
        options.include_inactive = 'true';
      }
      const result = await Assessments.loadPublicAssessments({ ...options });
      setOrgAssessments(result.data?.assessments || []);
    } else {
      setOrgAssessments([]);
    }
  }

  useEffect(() => {
    if (!isRegionSettled) return;
    setIsLoadingAssessments(true);
    try {
      loadPublicAssessments();
      setPublicAssessmentError(null);
    } catch (ex) {
      setPublicAssessmentError(ex);
    }
  }, [region, isRegionSettled]);

  useEffect(() => {
    if (!isRegionSettled) return;
    try {
      loadPrivateAssessments();
      setPrivateAssessmentError(null);
    } catch (ex) {
      setPrivateAssessmentError(ex);
    }
  }, [hasActiveSession, region, isRegionSettled, agencyClientId]);

  useEffect(() => {
    try {
      loadOrgAssessments();
      setOrgAssessmentError(null);
    } catch (ex) {
      setOrgAssessmentError(ex);
    }
  }, [hasActiveSession, includeInactive]);

  return {
    publicAssessmentError,
    privateAssessmentError,
    orgAssessmentError,
    assessments,
    isLoadingAssessments,
    userAssessments,
    orgAssessments
  };
};

export default useAssessments;
